import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Lock,
  ArrowForward,
  ExpandMore,
  ReceiptLong,
  AccountBalance,
  CurrencyRupee,
} from '@mui/icons-material';
import { fetchBalance, initiateRecharge, verifyRecharge, requestRefund, clearWalletError } from '../../store/slices/walletSlice';
import { openRazorpayCheckout, generateIdempotencyKey } from '../../utils/razorpay';
import { openCashfreeCheckout } from '../../utils/cashfree';
import walletService from '../../services/walletService';
import { useAnalytics } from '../../analytics/hooks';
import TransactionHistory from './TransactionHistory';
import BankAccountForm from './BankAccountForm';
import BankAccountList from './BankAccountList';

const PRESETS = [100, 200, 500, 1000, 2000, 5000];
const MIN = 10;
const MAX = 100000;

/* ── Shared style tokens ── */
const glass = {
  background: '#FFFFFF',
  border: '1px solid rgba(218,165,32,0.1)',
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(139,105,20,0.06)',
};

const WalletDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { balance, loading, error } = useSelector((s) => s.wallet);
  const reduxUser = useSelector((s) => s.auth?.user);

  const isAstrologer = reduxUser?.role === 'astrologer';

  const [amount, setAmount] = useState('');
  const [localError, setLocalError] = useState('');
  const [rechargeSuccess, setRechargeSuccess] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundConfirm, setRefundConfirm] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    walletService.recoverPending().then(res => {
      if (res.data?.recovered > 0) {
        dispatch(fetchBalance());
      }
    }).catch(() => {});
    dispatch(fetchBalance());
  }, [dispatch]);

  const handlePreset = (val) => {
    setAmount(String(val));
    setLocalError('');
    dispatch(clearWalletError('recharge'));
    track('wallet_recharge_initiated', { amount: val * 100 });
  };

  const handlePay = async () => {
    setLocalError('');
    setRechargeSuccess('');
    const num = parseFloat(amount);
    if (!num || num < MIN) { setLocalError(`Minimum recharge is ₹${MIN}`); return; }
    if (num > MAX) { setLocalError(`Maximum is ₹${MAX.toLocaleString()}`); return; }

    const idempotencyKey = generateIdempotencyKey('recharge');
    try {
      const result = await dispatch(initiateRecharge({ amount: num, currency: 'INR', idempotencyKey })).unwrap();
      const gateway = result.gateway || 'razorpay';
      track('payment_initiated', { amount: num * 100, gateway });

      if (gateway === 'cashfree') {
        const cfSandbox = result.key_id?.startsWith('TEST') ?? true;
        await openCashfreeCheckout({
          paymentSessionID: result.payment_session_id,
          orderID: result.order_id,
          sandbox: cfSandbox,
          onSuccess: async () => {
            const verifyKey = generateIdempotencyKey('verify');
            await dispatch(verifyRecharge({
              paymentData: { order_id: result.order_id },
              idempotencyKey: verifyKey,
            })).unwrap();
            navigate('/dashboard');
          },
          onFailure: (err) => setLocalError(err?.message || 'Payment failed. Please try again.'),
        });
      } else {
        await openRazorpayCheckout({
          keyId: result.key_id,
          orderId: result.order_id,
          amount: result.amount,
          currency: result.currency || 'INR',
          userEmail: reduxUser?.email || '',
          userName: reduxUser?.name || '',
          onSuccess: async (paymentResponse) => {
            const verifyKey = generateIdempotencyKey('verify');
            await dispatch(verifyRecharge({
              paymentData: {
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_signature: paymentResponse.razorpay_signature,
              },
              idempotencyKey: verifyKey,
            })).unwrap();
            navigate('/dashboard');
          },
          onDismiss: () => setLocalError('Payment cancelled.'),
        });
      }
    } catch {
      // error shown via Redux state
    } finally {
      walletService.recoverPending().then(res => {
        if (res.data?.recovered > 0) {
          dispatch(fetchBalance());
        }
      }).catch(() => {});
    }
  };

  const handleRefund = async () => {
    const num = parseFloat(refundAmount);
    if (!num || num <= 0) return;
    const idempotencyKey = generateIdempotencyKey('refund');
    try {
      await dispatch(requestRefund({ amount: num, reason: 'User requested refund', idempotencyKey })).unwrap();
      setRefundAmount('');
      setRefundConfirm(false);
      setRefundSuccess(`₹${num.toFixed(2)} refund initiated. Expect 5-7 business days.`);
    } catch {
      // error shown via Redux state
    }
  };

  const handleAccordion = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const displayError = localError || error.recharge;
  const selectedPreset = PRESETS.includes(parseFloat(amount)) ? parseFloat(amount) : null;

  /* ── Accordion shared styles ── */
  const accordionSx = {
    ...glass,
    mb: 2,
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: '0 0 16px 0' },
  };
  const accordionSummarySx = {
    '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 },
    minHeight: 56,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#FFFDF7',
        pb: 8,
        pt: 2,
      }}
    >
      <Container maxWidth="md">

        {/* ═══ Balance Hero Card ═══ */}
        <Box
          sx={{
            ...glass,
            p: { xs: 3, md: 4 },
            mb: 3,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative glow */}
          <Box
            sx={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(218,165,32,0.1) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />

          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AccountBalanceWallet sx={{ color: '#DAA520', fontSize: 24 }} />
                <Typography sx={{ color: '#9B8B7A', fontSize: 14, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
                  My Wallet
                </Typography>
              </Box>

              {loading.balance && !balance ? (
                <CircularProgress sx={{ color: '#DAA520' }} size={40} />
              ) : (
                <Typography
                  sx={{
                    color: '#2C1810',
                    fontSize: { xs: 40, md: 52 },
                    fontWeight: 800,
                    lineHeight: 1.1,
                    background: 'linear-gradient(135deg, #2C1810 30%, #8B6914)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  ₹{balance?.available?.toFixed(2) ?? '0.00'}
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              onClick={() => document.getElementById('add-money-section')?.scrollIntoView({ behavior: 'smooth' })}
              sx={{
                background: 'linear-gradient(135deg, #DAA520, #B8860B)',
                borderRadius: '28px',
                px: 3,
                py: 1.2,
                fontWeight: 700,
                fontSize: 14,
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(218,165,32,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FFD700, #DAA520)',
                  boxShadow: '0 6px 28px rgba(218,165,32,0.4)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              + Add Money
            </Button>
          </Box>

          {/* Stat chips */}
          <Box display="flex" gap={2} mt={3} flexWrap="wrap">
            {[
              { label: 'Available', value: `₹${(balance?.available ?? 0).toFixed(2)}`, color: '#00b894' },
              { label: 'Frozen', value: `₹${(balance?.frozen ?? 0).toFixed(2)}`, color: '#e17055' },
              { label: 'Currency', value: balance?.currency ?? 'INR', color: '#f9d71c' },
            ].map(({ label, value, color }) => (
              <Box
                key={label}
                sx={{
                  background: '#FFFDF7',
                  border: '1px solid rgba(218,165,32,0.08)',
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  minWidth: 100,
                }}
              >
                <Typography sx={{ color: '#9B8B7A', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {label}
                </Typography>
                <Typography sx={{ color, fontSize: 16, fontWeight: 700 }}>
                  {loading.balance && !balance ? '—' : value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ═══ Two-column: Add Money + Quick Actions ═══ */}
        <Box
          id="add-money-section"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
            gap: 3,
            mb: 3,
          }}
        >
          {/* ── Add Money Card ── */}
          <Box sx={{ ...glass, p: { xs: 2.5, md: 3 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={2.5}>
              <CurrencyRupee sx={{ color: '#DAA520', fontSize: 22 }} />
              <Typography sx={{ color: '#2C1810', fontWeight: 700, fontSize: 17 }}>
                Add Money
              </Typography>
            </Box>

            {/* Preset pills */}
            <Typography sx={{ color: '#9B8B7A', fontSize: 12, fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Quick Select
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1.5,
                mb: 3,
              }}
            >
              {PRESETS.map((p) => {
                const isSelected = selectedPreset === p;
                return (
                  <Box
                    key={p}
                    onClick={() => handlePreset(p)}
                    sx={{
                      textAlign: 'center',
                      py: 1.3,
                      borderRadius: '12px',
                      border: '2px solid',
                      borderColor: isSelected ? '#DAA520' : 'rgba(218,165,32,0.1)',
                      background: isSelected
                        ? 'rgba(218,165,32,0.12)'
                        : '#FFFDF7',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: isSelected ? '0 0 16px rgba(218,165,32,0.3)' : 'none',
                      '&:hover': {
                        borderColor: isSelected ? '#DAA520' : 'rgba(218,165,32,0.2)',
                        background: isSelected
                          ? 'rgba(218,165,32,0.15)'
                          : 'rgba(218,165,32,0.08)',
                        transform: 'scale(1.03)',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: isSelected ? '#8B6914' : '#6B5744',
                      }}
                    >
                      ₹{p.toLocaleString()}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Custom amount input */}
            <Typography sx={{ color: '#B8A898', fontSize: 12, textAlign: 'center', mb: 1.5, fontWeight: 500 }}>
              — or enter custom amount —
            </Typography>
            <TextField
              fullWidth
              type="number"
              size="small"
              placeholder={`Min ₹${MIN}`}
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setLocalError('');
                dispatch(clearWalletError('recharge'));
              }}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 0.5, fontWeight: 700, color: '#9B8B7A' }}>₹</Typography>
                ),
              }}
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  color: '#2C1810',
                  background: '#FFFDF7',
                  '& fieldset': { borderColor: 'rgba(218,165,32,0.15)' },
                  '&:hover fieldset': { borderColor: 'rgba(218,165,32,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#DAA520' },
                },
                '& input::placeholder': { color: '#B8A898' },
                '& input': { color: '#2C1810' },
              }}
            />

            {displayError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontSize: 13, background: 'rgba(255,82,82,0.1)', color: '#ff5252', border: '1px solid rgba(255,82,82,0.2)', '& .MuiAlert-icon': { color: '#ff5252' } }}>
                {displayError}
              </Alert>
            )}
            {rechargeSuccess && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: '12px', fontSize: 13, background: 'rgba(0,184,148,0.1)', color: '#00b894', border: '1px solid rgba(0,184,148,0.2)', '& .MuiAlert-icon': { color: '#00b894' } }}>
                {rechargeSuccess}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={loading.recharge || !amount || parseFloat(amount) < MIN}
              onClick={handlePay}
              endIcon={loading.recharge ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
              sx={{
                background: 'linear-gradient(135deg, #DAA520, #B8860B)',
                borderRadius: '14px',
                py: 1.5,
                fontWeight: 700,
                fontSize: 15,
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(218,165,32,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FFD700, #DAA520)',
                  boxShadow: '0 6px 28px rgba(218,165,32,0.4)',
                },
                '&:disabled': {
                  background: 'rgba(218,165,32,0.08)',
                  color: '#B8A898',
                  boxShadow: 'none',
                },
              }}
            >
              {loading.recharge
                ? 'Processing...'
                : amount
                ? `Pay ₹${parseFloat(amount).toLocaleString()}`
                : 'Select an Amount'}
            </Button>

            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mt={1.5}>
              <Lock sx={{ fontSize: 13, color: '#B8A898' }} />
              <Typography sx={{ fontSize: 11, color: '#B8A898' }}>
                Secured by Cashfree · 256-bit SSL
              </Typography>
            </Box>
          </Box>

          {/* ── Quick Actions ── */}
          <Box sx={{ ...glass, p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ color: '#9B8B7A', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
              Quick Actions
            </Typography>

            {[
              { icon: <ReceiptLong sx={{ fontSize: 20 }} />, label: 'Transaction History', panel: 'transactions', color: '#DAA520' },
              { icon: <AccountBalance sx={{ fontSize: 20 }} />, label: 'Refund to Bank/UPI', panel: 'refund', color: '#e17055' },
              ...(isAstrologer
                ? [
                    { icon: <AccountBalanceWallet sx={{ fontSize: 20 }} />, label: 'Bank Accounts', panel: 'banks', color: '#0984e3' },
                  ]
                : []),
            ].map(({ icon, label, panel, color }) => (
              <Box
                key={panel}
                onClick={() => setExpanded(expanded === panel ? false : panel)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: expanded === panel ? `${color}44` : 'rgba(218,165,32,0.08)',
                  background: expanded === panel ? `${color}11` : '#FFFDF7',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: `${color}44`,
                    background: `${color}0a`,
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `${color}15`,
                    color,
                  }}
                >
                  {icon}
                </Box>
                <Typography sx={{ color: '#2C1810', fontSize: 14, fontWeight: 600 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ═══ Expandable Sections ═══ */}

        {/* Transaction History */}
        <Accordion
          expanded={expanded === 'transactions'}
          onChange={handleAccordion('transactions')}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#9B8B7A' }} />} sx={accordionSummarySx}>
            <ReceiptLong sx={{ color: '#DAA520', fontSize: 20 }} />
            <Typography sx={{ color: '#2C1810', fontWeight: 600, fontSize: 15 }}>
              Transaction History
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <TransactionHistory />
          </AccordionDetails>
        </Accordion>

        {/* Refund */}
        <Accordion
          expanded={expanded === 'refund'}
          onChange={handleAccordion('refund')}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#9B8B7A' }} />} sx={accordionSummarySx}>
            <AccountBalance sx={{ color: '#e17055', fontSize: 20 }} />
            <Typography sx={{ color: '#2C1810', fontWeight: 600, fontSize: 15 }}>
              Refund to Bank / UPI
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ color: '#9B8B7A', fontSize: 13, mb: 2 }}>
              Refund unused wallet balance back to your original payment method. Processing takes 5-7 business days.
            </Typography>

            {refundSuccess && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: '12px', fontSize: 13, background: 'rgba(0,184,148,0.1)', color: '#00b894', border: '1px solid rgba(0,184,148,0.2)', '& .MuiAlert-icon': { color: '#00b894' } }}>
                {refundSuccess}
              </Alert>
            )}
            {error.refund && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontSize: 13, background: 'rgba(255,82,82,0.1)', color: '#ff5252', border: '1px solid rgba(255,82,82,0.2)', '& .MuiAlert-icon': { color: '#ff5252' } }}>
                {error.refund}
              </Alert>
            )}

            <Collapse in={!refundConfirm}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <TextField
                  size="small"
                  type="number"
                  placeholder="Amount"
                  value={refundAmount}
                  onChange={(e) => { setRefundAmount(e.target.value); dispatch(clearWalletError('refund')); setRefundSuccess(''); }}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, fontWeight: 700, color: '#9B8B7A' }}>₹</Typography> }}
                  sx={{
                    width: 160,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      color: '#2C1810',
                      background: '#FFFDF7',
                      '& fieldset': { borderColor: 'rgba(218,165,32,0.15)' },
                      '&:hover fieldset': { borderColor: 'rgba(225,112,85,0.4)' },
                      '&.Mui-focused fieldset': { borderColor: '#e17055' },
                    },
                    '& input': { color: '#2C1810' },
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!refundAmount || parseFloat(refundAmount) <= 0 || loading.refund}
                  onClick={() => setRefundConfirm(true)}
                  sx={{
                    borderColor: 'rgba(225,112,85,0.4)',
                    color: '#e17055',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 0.9,
                    '&:hover': { borderColor: '#e17055', background: 'rgba(225,112,85,0.08)' },
                  }}
                >
                  Request Refund
                </Button>
              </Box>
            </Collapse>

            <Collapse in={refundConfirm}>
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px', fontSize: 13, background: 'rgba(249,215,28,0.08)', color: '#f9d71c', border: '1px solid rgba(249,215,28,0.2)', '& .MuiAlert-icon': { color: '#f9d71c' } }}>
                Confirm refund of <strong>₹{parseFloat(refundAmount || 0).toFixed(2)}</strong>?
                This will deduct from your wallet and initiate a gateway refund.
              </Alert>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={loading.refund}
                  onClick={handleRefund}
                  sx={{
                    background: '#e17055',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { background: '#d63031' },
                  }}
                >
                  {loading.refund ? <CircularProgress size={16} color="inherit" /> : 'Yes, Refund'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setRefundConfirm(false)}
                  sx={{ borderRadius: '10px', textTransform: 'none', borderColor: 'rgba(218,165,32,0.2)', color: '#6B5744' }}
                >
                  Cancel
                </Button>
              </Box>
            </Collapse>
          </AccordionDetails>
        </Accordion>

        {/* Astrologer-only: Bank Accounts */}
        {isAstrologer && (
          <Accordion
            expanded={expanded === 'banks'}
            onChange={handleAccordion('banks')}
            sx={accordionSx}
          >
            <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#9B8B7A' }} />} sx={accordionSummarySx}>
              <AccountBalanceWallet sx={{ color: '#0984e3', fontSize: 20 }} />
              <Typography sx={{ color: '#2C1810', fontWeight: 600, fontSize: 15 }}>
                Bank Accounts
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 3fr' }, gap: 3 }}>
                <Box>
                  <Typography sx={{ color: '#6B5744', fontWeight: 600, fontSize: 14, mb: 2 }}>
                    Add Bank Account
                  </Typography>
                  <BankAccountForm />
                </Box>
                <Box>
                  <Typography sx={{ color: '#6B5744', fontWeight: 600, fontSize: 14, mb: 2 }}>
                    Saved Accounts
                  </Typography>
                  <BankAccountList />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

      </Container>
    </Box>
  );
};

export default WalletDashboard;
